import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FaSpinner } from 'react-icons/fa';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// import businessPrimeJSON from '../../../../BusinessPrime.json';
import Logo from "../../../../assets/images/LogoNew.svg";
import { ApplicantHeaderActions, createApplicationFormUpdateHandler } from '../../../../components/ApplicationInvite';
import AutoSaveIndicator from '../../../../components/AutoSaveIndicator/AutoSaveIndicator';
import { FORM_AUTOFILL_PROPS } from '../../../../components/InputField/autofillProps';
import ReviewShellBoundary from '../../../../components/ReviewWizard/ReviewShellBoundary';
import { getAccountActivityRuleErrors, getRequiredErrorMessage, shouldShowField, validateField as validateJsonField } from '../../../../components/InputField/helper';
import useAutoSave from '../../../../hooks/useAutoSave';
import useFormReviewShell from '../../../../hooks/useFormReviewShell';
import useUnsavedChangesWarning from '../../../../hooks/useUnsavedChangesWarning';
import { DialogContext } from '../../../../store/context/DialogContext';
import { doGET, doPOST, doPUT } from '../../../../util/HttpUtil';
import AboutBusiness from './components/AboutBusiness';
import AccountActivity from './components/AccountActivity';
import AssetsInvolvement from './components/AssetsInvolvement';
import BusinessType from './components/BusinessType';
import ComplianceDetails from './components/ComplianceDetails';
import ConsoleAccordion from './components/ConsoleAccordion';
import CorporateAccountUpgrade from './components/CorporateAccountUpgrade';
import Documents from './components/DocumentsPages';
import FundFamilyOffices from './components/FundFamilyOffices';
import MoneyServicesBusiness from './components/MoneyServicesBusiness';
import { getBusinessTypeChecks } from './businessTypeUtils';
import { calculateNextStep, calculatePrevStep, convertArrayToDict } from './helper';
import ThankYou from './components/ThankYou';
import StartAgain from './components/StartAgain';
import { Button } from 'reactstrap';
import { ThreeBounce } from 'better-react-spinkit';
import "./styles.css"
import ExtraQuestions from '../ExtraQuestions';
import { hasActiveAsks, hasPendingAsks } from '../askIdentity';
import ApplicationDocRedo from '../components/IndividualLite/ApplicationDocRedo';
import { AskMoreMessage } from '../components';
import { handleUtmFormSubmit } from '../../../../util/utm';
import {
  clearAuthResume,
  clearAuthTokens,
  clearStoredAuthMessage,
  clearVerifyIndividualContext,
  markExplicitLogoutLanding,
  setLegacyUnauthUrl,
} from '../../../../util/authResume';
import { STEP_NUM } from '../BPlushelper';
import { STEP_STATUS, STEPS } from '../IPlushelper';
import { createBusinessPrimeAutoSaveAdapter } from '../autosave/buildBusinessPrimeAutoSavePayload';
import { buildPostAutoSaveUpdatePayload, createAutoSaveFieldDefinitionMap, getAutoSaveProceedBlockedMessage, isAutoSaveEnabledForConfig, resolveAutoSaveDebounceMs } from '../autosave/shared';
import { SUBMIT_DOC_FLOW, validateSubmitDocuments } from '../submitDocumentValidation';


const COMPONENTS = {
  BusinessType,
  AboutBusiness,
  AccountActivity,
  MoneyServicesBusiness,
  AssetsInvolvement,
  ComplianceDetails,
  FundFamilyOffices,
  CorporateAccountUpgrade,
  Documents,
  ThankYou,
  StartAgain,
  ExtraQuestions,
  AskMoreMessage,
  ApplicationDocRedo
};

const getFirstOrderedFieldErrorMessage = (orderedFields = [], errorMap = {}) => {
  for (const fieldPath of orderedFields) {
    const fieldError = errorMap?.[fieldPath];
    if (fieldError) {
      return fieldError;
    }
  }

  return Object.values(errorMap || {}).find(Boolean) || null;
};

const ApplyBusinessPrime = ({ asLabel, editMode = false, formId, refreshId, onChange = () => { } }) => {
  const { edit_id } = useParams();
  const [data, setData] = useState({});
  const [showErr, setShowErr] = useState(false);
  const [errors, setErrors] = useState({});
  const { showError } = useContext(DialogContext);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [step, setStep] = useState({ wizard: "BusinessType" });
  const [editId, setEditID] = useState(edit_id ?? formId);
  const [hasError, setHasError] = useState(false);
  const [validationkeyValue, setValidationKeyValue] = useState({});
  const [businessPrimeValidationFields, setBusinessPrimeValidationFields] = useState([]);
  const [autoSaveConfig, setAutoSaveConfig] = useState(null);
  const [applicationSteps, setApplicationSteps] = useState({});
  const [applicationForm, setApplicationForm] = useState({});
  const navigate = useNavigate();
  const pageNumberRef = useRef(1);
  const location = useLocation();
  const {
    reviewError,
    reviewLoading,
    reviewNavigation,
  } = useFormReviewShell({
    applicationId: editId,
    autoLoad: !asLabel && !editMode && Boolean(editId),
    fieldDefinitions: validationkeyValue,
    formData: data,
    currentWizard: step?.wizard,
    onNavigateToWizard: (wizard) => {
      setStep({ wizard });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onPersistError: showError,
  });

  useEffect(() => {
    const qsStep = new URLSearchParams(location.search).get('step');
    if (qsStep == "Documents") {
      setStep({ wizard: "Documents" });
    }
  }, [location.search]);

  // TO CALCULATE INITIAL REQUIRED ERRORS FOR EMPTY FIELDS, AND FOR CONDITIONAL FIELDS RENEDRING WITH CHANGE OF DATA
  useEffect(() => {
    const initialErrors = {};
    businessPrimeValidationFields.map((field) => {
      if (
        field.required &&
        field?.wizard === step?.wizard &&
        shouldShowField(data, field.showIf) &&
        (data[field.field] === undefined || data[field.field] === null || data[field.field] === "")
      ) {
        // Check if the value is not a valid false boolean
        if (data[field.field] !== false && !errors?.[field.field]) {
          initialErrors[field.field] = getRequiredErrorMessage(field, "This field is required.");
        }
      }
    });
    const shownErrors = {};
    businessPrimeValidationFields.map((field) => {
      if (field?.wizard === step?.wizard && shouldShowField(data, field.showIf)) {
        const error = validateJsonField(field, data[field.field], data)
        if (error) {
          shownErrors[field.field] = error;
        }
      }
    });
    setErrors({ ...shownErrors, ...initialErrors });
  }, [data, step, businessPrimeValidationFields]);

  // TO GET BUSINESS PRIME FIELD JSON FROM API, AND SAVE IT IN A STATE
  const getValidationJSON = async () => {
    try {
      const response = await doGET("/api/cyclos/fields-json?group=BusinessPrime");
      if (response.status === 200) {
        // const bPrimeJSON = businessPrimeJSON;
        const bPrimeJSON = response?.data ?? {};
        setAutoSaveConfig(bPrimeJSON?.autoSave || null);
        setBusinessPrimeValidationFields(bPrimeJSON?.fields);
        const fieldsMap = createAutoSaveFieldDefinitionMap(bPrimeJSON?.fields);
        setValidationKeyValue(fieldsMap);
      }

    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    getValidationJSON();
  }, []);

  useEffect(() => {
    if (formId) {
      setEditID(formId);
    }
  }, [formId]);

  const handleChange = (value) => {
    setData(value)
  };

  const getVisibleStepFieldOrder = () => (
    businessPrimeValidationFields
      .filter((field) => field?.wizard === step?.wizard && shouldShowField(data, field?.showIf))
      .map((field) => field.field)
  );

  // TO CHECK IF THERE IS AN ERROR IN ANY FIELD, IF YES DISPLAY IT SEQUENCE WISE 
  const checkNext = () => {
    setShowErr(true);
    const firstErrorMessage = getFirstOrderedFieldErrorMessage(getVisibleStepFieldOrder(), errors);
    if (firstErrorMessage) {
      showError(firstErrorMessage);
      return false;
    }
    setErrors({});
    return true;

  };

  const handleSubmit = async ({ isLastStep }) => {
    if (step?.wizard === "AccountActivity") {
      // Cross-field validation check (location↔activity, number↔amount pairs)
      const crossFieldErrors = getAccountActivityRuleErrors(data);
      if (crossFieldErrors.length) {
        setShowErr(true);
        const mapped = crossFieldErrors.reduce((acc, err) => {
          const fields = Array.isArray(err.activityFields) && err.activityFields.length ? [...err.activityFields, err.field] : [err.field];
          fields.filter(Boolean).forEach((field) => {
            acc[field] = err.message;
          });
          return acc;
        }, {});
        const mergedErrors = { ...errors, ...mapped };
        const firstErrorMessage = getFirstOrderedFieldErrorMessage(getVisibleStepFieldOrder(), mergedErrors) || crossFieldErrors[0]?.message;
        showError(firstErrorMessage);
        setErrors((prev) => ({ ...prev, ...mapped }));
        return;
      }
    }
    if (checkNext()) {
      setErrors({});
      let nextStep = calculateNextStep(data, step?.wizard, editMode, applicationForm?.cyclosUserId);
      if (nextStep?.wizard == "PayNow") {
        await handleUpdate({ nextStep: { wizard: "Documents" }, data, isLastStep: false });
        navigate(`/business-plus/cart/${applicationForm?.plus_id}/${editId}?goto=/business-prime/${edit_id}?step=Documents`, { replace: true });
      } else {
        await handleUpdate({ nextStep: nextStep, data, isLastStep });
      }
      setShowErr(false);
    }
  };

  const handlePrev = (v) => {
    setErrors({})
    let prevStep = calculatePrevStep(data, step?.wizard)
    pageNumberRef.current = pageNumberRef.current - 1
    setStep(prevStep);
  };

  const checkIsAskForInfo = (steps) => {
    return (steps ?? [])?.find((v) => v?.status == 2)
  }


  const getCarts = async (application_id) => {
    try {
      const res = await doGET(`/api/cart/grid?application_id=${application_id}`);
      let allCarts = res.data.rows || [];

      if (!allCarts?.length) {
        const res = await doGET(`/api/cart/app/create?id=${application_id}`);
        allCarts = [res.data ?? {}];
      }

      if (allCarts?.length) {
        const unpaidCarts = allCarts.filter((c) => c.status == 0);
        const paidCarts = allCarts.filter((c) => c.status == 1);
        return ({
          unpaidCarts,
          paidCarts,
        });
      }

    } catch (err) {
      console.error('Error fetching carts:', err);
    }
  }

  const fetchDetail = async (stepProp, { syncUi = true } = {}) => {
    if (syncUi) {
      setDataLoading(true);
    }
    try {
      if (editId) {
        localStorage.setItem("formId", editId);
        const response = await doGET(`/api/business-prime/detail?id=${editId}`);
        let businessPrimeResponse = response.data ?? {}
        const applicationResponse = await doGET(`/api/application-form/detail?id=${editId}&withExtension=true`);
        if (applicationResponse?.status == 200) {
          const applicationFormResponse = applicationResponse?.data
          let businessPlusApplication;
          let carts = {};
          if (syncUi && applicationFormResponse?.plus_id && !applicationFormResponse?.cyclosUserId) {
            const businessPlusResponse = await doGET(`/api/application-form/detail?id=${applicationFormResponse?.plus_id}&withExtension=true`);
            if (businessPlusResponse?.status == 200) {
              businessPlusApplication = businessPlusResponse?.data
            }
            carts = await getCarts(applicationFormResponse?.plus_id);
          }
          if (syncUi) {
            setApplicationForm({ ...applicationResponse?.data, businessPlusApplication: (businessPlusApplication ?? {}), bPlusCarts: (carts ?? {}) })
          }
          if (syncUi && applicationFormResponse?.steps?.length) {
            const askInfoStep = checkIsAskForInfo(applicationFormResponse?.steps)
            const stepsObj = convertArrayToDict(applicationFormResponse?.steps)
            setApplicationSteps(stepsObj);
            const applicationFormStepStatus = stepsObj[STEPS.APPLICATION_FORM]?.status;
            const documentVerificationStepStatus = stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status;
            const isDocumentRedoRequired = applicationFormStepStatus > STEP_STATUS.DRAFT
              && [STEP_STATUS.DRAFT, STEP_STATUS.INCOMPLETE].includes(documentVerificationStepStatus);
            if (
              !asLabel
              && !editMode
              && documentVerificationStepStatus > STEP_STATUS.DRAFT && [STEP_STATUS.SUBMITTED, STEP_STATUS.APPROVED, STEP_STATUS.REJECTED].includes(documentVerificationStepStatus)
              && applicationFormStepStatus > STEP_STATUS.DRAFT && [STEP_STATUS.SUBMITTED, STEP_STATUS.APPROVED, STEP_STATUS.REJECTED].includes(applicationFormStepStatus)
            ) {
              if (carts?.unpaidCarts?.length) {
                navigate(`/business-plus/cart/${applicationFormResponse?.plus_id}/${editId}?goto=/business-plus/${applicationFormResponse?.plus_id}?step=${STEP_NUM.REFERRAL_AND_SOURCE}`, { replace: true });
                return;

              }
              if (businessPlusApplication && applicationFormResponse?.plus_id) {
                const bPlusAskInfoStep = checkIsAskForInfo(businessPlusApplication?.steps)
                const bPlusStepsObj = convertArrayToDict(businessPlusApplication?.steps)

                if (bPlusStepsObj[STEPS.APPLICATION_FORM]?.status > 0 && Object.keys(bPlusAskInfoStep ?? {}).length && [STEPS.APPLICATION_FORM, STEPS.DOCUMENT_VERIFICATION]?.includes(bPlusAskInfoStep?.label)) {
                  navigate(`/business-plus/${applicationFormResponse?.plus_id}`, { replace: true });
                  return;
                }
              }

              setStep({ wizard: "ThankYou" });
            }

            if (!asLabel && !editMode && isDocumentRedoRequired) {
              setStep({ wizard: "ApplicationDocRedo" });
            } else if (!asLabel && !editMode && hasPendingAsks(response?.data?.asks) && askInfoStep?.status == STEP_STATUS.INCOMPLETE && applicationFormStepStatus == STEP_STATUS.INCOMPLETE) {
              setStep({ wizard: "AskMoreMessage" });
            }
          }
        }
        if (syncUi) {
          setErrors({});
        }
        const nextData = { ...response?.data };
        if (syncUi) {
          setData(nextData);
        }
        return nextData;
      }
    } catch (error) {
      console.log(error);
    } finally {
      if (syncUi) {
        setDataLoading(false);
        if (stepProp) setStep(stepProp);
      }
    }
    return null;
  }

  const isAutoSaveEnabled = !asLabel
    && !!editId
    && !!data?._id
    && !loading
    && !dataLoading
    && isAutoSaveEnabledForConfig({
      autoSaveConfig,
      step,
    });
  const autoSaveDebounceMs = resolveAutoSaveDebounceMs(autoSaveConfig);

  const autoSaveAdapter = useMemo(
    () => createBusinessPrimeAutoSaveAdapter(validationkeyValue, autoSaveConfig),
    [autoSaveConfig, validationkeyValue]
  );

  const {
    autoSaveStatus,
    lastSavedAt,
    pendingChangesCount,
    pendingChanges,
    hasUnsavedChanges,
    isAutoSaveNavigationPending,
    flushAndPauseAutoSave,
    resumeAutoSave,
  } = useAutoSave({
    data,
    isEnabled: isAutoSaveEnabled,
    debounceMs: autoSaveDebounceMs,
    adapter: autoSaveAdapter,
    onDirtyUpdate: async () => await fetchDetail(undefined, { syncUi: false }),
    onDataReconcile: setData,
    onFieldPersisted: reviewNavigation?.commitFieldTask,
  });

  useUnsavedChangesWarning({
    when: hasUnsavedChanges || loading,
    isSaving: autoSaveStatus === 'saving' || autoSaveStatus === 'resyncing',
    pendingChangesCount,
  });

  const nextActionLoading = loading || isAutoSaveNavigationPending;
  const currentPageReviewTasks = reviewNavigation?.currentWizardActionableTasks || [];

  useEffect(() => {
    fetchDetail();
  }, [editId, refreshId]);

  const handleUpdate = async ({ nextStep, payload, isfetchDetail, isLastStep = false }) => {
    if (editId) {
      setLoading(true);
      setHasError(false);
      const autoSaveResult = await flushAndPauseAutoSave();
      if (!autoSaveResult?.ok) {
        setLoading(false);
        setHasError(true);
        showError(getAutoSaveProceedBlockedMessage(autoSaveResult?.reason));
        return;
      }
      const latestData = autoSaveResult?.latestData || data;
      const refreshedCurrentPageReviewTasks = reviewNavigation?.peekCurrentWizardActionableTasks?.()
        || currentPageReviewTasks;
      const refreshedActionableReviewTasks = reviewNavigation?.peekActionableTasks?.()
        || reviewNavigation?.actionableTasks
        || [];
      if (nextStep && !isLastStep && !asLabel && !editMode && refreshedCurrentPageReviewTasks.length) {
        const blockedCurrentPageTask = reviewNavigation?.peekPreferredActionableTask?.(reviewNavigation?.currentWizard || "")
          || refreshedCurrentPageReviewTasks[0];
        resumeAutoSave(latestData);
        setLoading(false);
        showError("Please finish the remaining items on this page before continuing.");
        if (blockedCurrentPageTask) {
          reviewNavigation?.goToTask(blockedCurrentPageTask);
        }
        return { status: 400 };
      }
      if (isLastStep && !asLabel && !editMode && refreshedActionableReviewTasks.length) {
        const blockedActionableTask = reviewNavigation?.peekPreferredActionableTask?.()
          || refreshedActionableReviewTasks[0];
        resumeAutoSave(latestData);
        setLoading(false);
        showError("Please complete all requested items before submitting.");
        if (blockedActionableTask) {
          reviewNavigation?.goToTask(blockedActionableTask);
        }
        return { status: 400 };
      }
      let submissionData = latestData;
      try {
        if (!asLabel) {
          const updatePayload = buildPostAutoSaveUpdatePayload(latestData, payload);
          const response = await doPUT(`/api/business-prime/update`, updatePayload ?? { ...latestData, _id: editId });
          if (response?.status == 200) {
            const nextData = { ...response?.data };
            setData(nextData);
            submissionData = nextData;
            resumeAutoSave(nextData);

            if (isLastStep && !asLabel && !editMode) {
              const documentValidation = await validateSubmitDocuments({
                flow: SUBMIT_DOC_FLOW.BUSINESS_PRIME_APPLICATION,
                recordId: editId,
                data: submissionData,
                applicationForm,
              });

              if (!documentValidation?.isValid) {
                setHasError(true);
                showError(documentValidation?.errors?.[0]?.error || "Please upload all required documents before submitting.");
                return { status: 400 };
              }

              const submitResponse = await doGET(`/api/business-prime/submit?id=${editId}&markFormSubmit=true&markDocSubmit=true`)
              if (submitResponse?.status == 200) {
                await reviewNavigation?.markVisibleTasksResponded?.({ quiet: true });
                handleUtmFormSubmit({
                  formType: "BUSINESS_PRIME", utmPayload: {
                    utm_source: applicationForm?.extension?.utm_source,
                    utm_medium: applicationForm?.extension?.utm_medium,
                    utm_campaign: applicationForm?.extension?.utm_campaign,
                    utm_content: applicationForm?.extension?.utm_content,
                    utm_term: applicationForm?.extension?.utm_term,
                  }
                });
                if (nextStep) {
                  pageNumberRef.current = 1;
                  await fetchDetail();
                  setLoading(false);
                }
              }
            } else {
              pageNumberRef.current = pageNumberRef.current + 1;
              setLoading(false);
              setStep(nextStep);
            }
          } else {
            resumeAutoSave(null);
          }
        }
      } catch (error) {
        resumeAutoSave(null);
        setHasError(true);
        showError(error);
      } finally {
        setLoading(false);
      }
    } else {
      resumeAutoSave(null);
      if (nextStep) {
        setStep(nextStep);
      }
    }
  };

  const logout = async () => {
    try {
      const response = await doPOST("/auth/logout");
      if (response?.status == 200) {
        clearAuthTokens();
        clearAuthResume();
        clearStoredAuthMessage();
        clearVerifyIndividualContext();
        setLegacyUnauthUrl("");
        markExplicitLogoutLanding();
        window.location = "/"
      }
    } catch (error) {
      showError(error)
    }
  }

  const renderAsAccordion = () => {
    const { msbCheck, cryptoExchangeCheck, fundAndFamilyCheck, accountUpgradeCheck } = getBusinessTypeChecks(data?.businesses);

    const commonProps = {
      decrementStep: handlePrev,
      recordId: edit_id,
      bPrimeJSON: validationkeyValue,
      data,
      handleChange,
      pageNum: pageNumberRef.current,
      loading,
      step,
      handleSubmit,
      setErrors,
      showErr,
      asLabel: true && !editMode,
      editMode: editMode,
      hasError,
      applicationForm,
      errors
    };


    const extraQuestionsProps = {
      dataLoading: loading || dataLoading,
      formId: edit_id,
      formType: "BUSINESS_PRIME",
      onSubmit: () => { fetchDetail({ wizard: "ThankYou" }) },
      questionIds: data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? []
    }

    const steps = [
      <BusinessType label="Business Type" {...commonProps} />,
      <AboutBusiness label="About business" {...commonProps} />,
      <AccountActivity label="Expected Account Activity"{...commonProps} />,
      (msbCheck || cryptoExchangeCheck) && <MoneyServicesBusiness label="Money Services Business" {...commonProps} />,
      (cryptoExchangeCheck) && <AssetsInvolvement label="Cryptocurrency and Digital Assets Involvement" {...commonProps} />,
      (fundAndFamilyCheck) && <FundFamilyOffices label="Funds and Family Offices" {...commonProps} />,
      (accountUpgradeCheck) && <CorporateAccountUpgrade label="Corporate Account Upgrade" {...commonProps} />,
      (fundAndFamilyCheck || msbCheck || cryptoExchangeCheck) && <ComplianceDetails label="Compliance" {...commonProps} />,
      hasActiveAsks(data?.asks) && <ExtraQuestions label="Extra Infromation" {...commonProps} {...extraQuestionsProps} />
    ];

    return <ConsoleAccordion steps={steps.filter(Boolean)} />;
  };


  const CurrentComponent = COMPONENTS[step.wizard];
  const shouldShowReviewWizard = !asLabel
    && !editMode
    && reviewNavigation?.hasVisibleTasks
    && !["ThankYou", "AskMoreMessage", "ExtraQuestions", "ApplicationDocRedo"].includes(step?.wizard);

  return (
    <div className={!asLabel && !editMode ? 'form-content-container xl' : ""}>

      <div className='form-content-header'>
        {(!asLabel && !editMode) && <img
          src={Logo}
          alt={("fv-bank")}
          className="logo logo-dark form-content-logo"
        />}

        {asLabel || editMode ? <></> : (
          <ApplicantHeaderActions
            applicationForm={applicationForm}
            formData={data}
            formId={edit_id}
            formType={applicationForm?.formType}
            onApplicationFormUpdated={createApplicationFormUpdateHandler(setApplicationForm)}
            disabled={asLabel || editMode}
            logoutComponent={(
              <div className='logout-btn-form'>
                <Button
                  className='form-content-btn'
                  onClick={logout}
                  disabled={loading}
                >
                  {loading ? <ThreeBounce color="#FFF" /> : ("Logout")}
                </Button>
              </div>
            )}
          />
        )}

      </div>

      {dataLoading ?
        <div className=" form-content-body  d-flex justify-content-center align-items-center flex-1">
          <FaSpinner size={20} className="spinner" />
        </div>
        :
        <Form className={asLabel || editMode ? "" : 'form-content-body '} noValidate {...FORM_AUTOFILL_PROPS}>
          <>
            {(!asLabel && !editMode) && <div className='form-content-body-header'>
              <div className="heading">{("Business Prime Account Application")}</div>
              <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={lastSavedAt} pendingChangesCount={pendingChangesCount} pendingChanges={pendingChanges} />
            </div>}

              <div className='form-content-body-content'>
              {asLabel ? renderAsAccordion() : (
                <ReviewShellBoundary
                  reviewNavigation={reviewNavigation}
                  loading={reviewLoading}
                  error={reviewError}
                  showReviewWizard={shouldShowReviewWizard}
                >
                  {CurrentComponent ? (
                    <CurrentComponent
                      decrementStep={handlePrev}
                      applicationForm={applicationForm}
                      recordId={edit_id}
                      bPrimeJSON={validationkeyValue}
                      asLabel={asLabel}
                      data={data}
                      handleChange={handleChange}
                      pageNum={pageNumberRef.current}
                      loading={nextActionLoading}
                      step={step}
                      handleSubmit={handleSubmit}
                      setErrors={setErrors}
                      showErr={showErr}
                      startAgain={() => {
                        pageNumberRef.current = 1;
                        setStep({ wizard: "BusinessType" });
                      }}
                      dataLoading={nextActionLoading || dataLoading} // for extraquestions
                      formId={edit_id} // for extraquestions
                      formType={"BUSINESS_PRIME"} // for extraquestions
                      remark={(data?.remark ?? "")} // for askMoreMessage
                      onNext={() => {
                        setStep({ wizard: "ExtraQuestions" }) // for askMoreMessage
                      }}
                      onSubmit={() => { fetchDetail({ wizard: "ThankYou" }) }} // for extraquestions
                      questionIds={data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? []}  // for extraquestions
                      type={"Business Prime"} // for ApplicationDocRedo
                      hideContainerStyle // for ApplicationDocRedo
                      title="Business Prime"
                      errors={errors}
                    />
                  ) : null}
                </ReviewShellBoundary>
              )}
            </div>
          </>
        </Form>
      }
    </div>
  );
};

export default ApplyBusinessPrime;
